import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Divider,
  Flex,
  Heading,
  Input,
  Skeleton,
  Spacer,
  Text,
  useToast,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import {
  finleyAssert,
  error as commonError,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { typeguards, Assignment } from '@finley-technologies/finley-types';

import {
  useCreateOrUpdateAssignmentStrategyMutation,
  useCreateReceivableResponseTapePreviewsFromAssignmentStrategyMutation,
} from 'types/dashboard-core-gqlTypes.generated.d';

import type {
  BorrowingBaseMechanicsForLoanProduct,
  EligibilityExclusion,
  ExclusionSqlClauseState,
  LoanProductCriteria,
  PossibleLoanProductCriteria,
} from '../../types';
import { DecisionHeuristicByTotalAssignableExposure } from './DecisionHeuristicTotalAssignableExposure';
import { EligibilityExclusions } from './EligibilityExclusions';
import { ExclusionCriteriaSection } from './ExclusionCriteriaSection';
import {
  initWaterfallByExcessMargin,
  initTotalAssignableExposureDecisionHeuristic,
} from './utils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { Select } from 'packages/common-frontend/components/Select';
import {
  successToastConfig,
  errorToastConfig,
} from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetCreditFacilitiesAndLoanProductsWithBorrowingBaseMechanicsForAssignmentGroupingQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { useGetLatestInitialAssignmentSummaryLazyQueryGQLType } from 'packages/loanProduct/requests/requests.gql.generated';
import { WaterfallByExcessMargin } from 'packages/loanProduct/ui/assignmentStrategy/assignmentStrategyForm/WaterfallByExcessMargin';
import { parseAssignmentSummaryPreview } from 'packages/loanProduct/ui/v2/AssignmentSummary/utils';

export const AssignmentStrategyForm = ({
  implementationId,
  strategyId,
  initialStrategyName,
  initialDescription,
  initialLoanProductCriteria,
  initialEligibilityExclusions,
  initialDecisionHeuristic,
  refetchAssignmentStrategy,
  backUrl,
  readOnly,
}: {
  implementationId: string;
  strategyId?: string;
  initialStrategyName?: string;
  initialDescription?: string;
  initialLoanProductCriteria?: LoanProductCriteria[];
  initialEligibilityExclusions?: EligibilityExclusion[];
  initialDecisionHeuristic?: Assignment.DecisionHeuristicConfig;
  refetchAssignmentStrategy?: Function;
  backUrl: string;
  readOnly: boolean;
}) => {
  const navigate = useNavigate();
  const toast = useToast();

  const [strategyName, setStrategyName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [strategyNameError, setStrategyNameError] = useState<boolean>(false);
  const [
    showDuplicateLoanProductCriteriaAlert,
    setShowDuplicateLoanProductCriteriaAlert,
  ] = useState<boolean>(false);
  const [
    showIncompleteLoanProductCriteriaAlert,
    setShowIncompleteLoanProductCriteriaAlert,
  ] = useState<boolean>(false);
  const [
    showExclusionSqlClauseRuleErrors,
    setShowExclusionSqlClauseRuleErrors,
  ] = useState<boolean>(false);
  const [
    showInvalidExclusionSqlClausesAlert,
    setShowInvalidExclusionSqlClausesAlert,
  ] = useState<boolean>(false);
  const [
    showInvalidHeuristicAllocationsAlert,
    setShowInvalidHeuristicAllocationsAlert,
  ] = useState<boolean>(false);

  const [
    showNegativeWaterfallValueAlert,
    setShowNegativeWaterfallValueAlert,
  ] = useState<boolean>(false);

  const [
    heuristicConfig,
    setHeuristicConfig,
  ] = useState<Assignment.DecisionHeuristicConfig>();

  const initialLoanProductCriteriaMap = useMemo(() => {
    const loanProductCriteriaMap = new Map<string, LoanProductCriteria>();
    initialLoanProductCriteria?.forEach((item) => {
      loanProductCriteriaMap.set(item.id, item);
    });
    return loanProductCriteriaMap;
  }, [initialLoanProductCriteria]);

  const [currentLoanProductCriteria, setCurrentLoanProductCriteria] = useState<
    PossibleLoanProductCriteria[]
  >([]);

  const initialEligibilityExclusionMap = useMemo(() => {
    const eligibilityExclusionMap = new Map<string, string>();
    initialEligibilityExclusions?.forEach((item) => {
      const itemKey = `${item.creditFacilityId}+${item.loanProductId}+${item.eligibilityCriteriaKey}`;
      eligibilityExclusionMap.set(itemKey, item.id);
    });
    return eligibilityExclusionMap;
  }, [initialEligibilityExclusions]);

  const [
    currentEligibilityExclusionsMap,
    setCurrentEligibilityExclusionsMap,
  ] = useState<Map<string, Set<string>>>(new Map<string, Set<string>>());

  const currentExclusionSqlClauseStates = useRef<
    Map<number, ExclusionSqlClauseState>
  >(new Map<number, ExclusionSqlClauseState>());

  const [
    validLoanProductsForCreditFacilitiesMap,
    setValidLoanProductsForCreditFacilitiesMap,
  ] = useState<Map<string, string[]>>();

  const [loanProductNameMap, setLoanProductNameMap] = useState<
    Map<string, string>
  >();

  // key is `${creditFacilityId}+${loanProductId}`
  const [borrowingBaseMechanicsMap, setBorrowingBaseMechanicsMap] = useState<
    Map<string, BorrowingBaseMechanicsForLoanProduct>
  >(new Map<string, BorrowingBaseMechanicsForLoanProduct>());

  useEffect(() => {
    setStrategyName(initialStrategyName ?? '');
    setDescription(initialDescription ?? '');
    setCurrentLoanProductCriteria(initialLoanProductCriteria ?? []);
    if (initialEligibilityExclusions) {
      const eligibilityExclusionsMap = new Map<string, Set<string>>();
      initialEligibilityExclusions.forEach((exclusion) => {
        const eligibilityExclusionsSet =
          eligibilityExclusionsMap.get(
            `${exclusion.creditFacilityId}+${exclusion.loanProductId}`,
          ) ?? new Set<string>();
        eligibilityExclusionsSet.add(exclusion.eligibilityCriteriaKey);
        eligibilityExclusionsMap.set(
          `${exclusion.creditFacilityId}+${exclusion.loanProductId}`,
          eligibilityExclusionsSet,
        );
      });
      setCurrentEligibilityExclusionsMap(eligibilityExclusionsMap);
    }
  }, [
    initialDescription,
    initialEligibilityExclusions,
    initialLoanProductCriteria,
    initialStrategyName,
  ]);

  useEffect(() => {
    setShowDuplicateLoanProductCriteriaAlert(false);
    setShowIncompleteLoanProductCriteriaAlert(false);
  }, [currentLoanProductCriteria]);

  const [
    creditFacilityIdForBorrower,
    setCreditFacilityIdForBorrower,
  ] = useState<string>();

  const [
    totalAssignableExposureByLoanProductId,
    setTotalAssignableExposureByLoanProductId,
  ] = useState<{ [loanProductId: string]: number }>();

  const {
    data: creditFacilitiesForAssignmentGroupingData,
    loading: creditFacilitiesForAssignmentGroupingDataLoading,
  } = useGetCreditFacilitiesAndLoanProductsWithBorrowingBaseMechanicsForAssignmentGroupingQuery(
    {
      /**
       *  Programmatically added onError handler.
       * @onError-TODO: Explicitly handle errors for this fetch
       */
      onError: (err) => {
        errorToastConfig.title = getUnexpectedErrorMessage(err);
        toast(errorToastConfig);
      },
      variables: {
        implementationId,
      },
      onCompleted: (data) => {
        const newBorrowingBaseMechanicsMap = new Map<
          string,
          BorrowingBaseMechanicsForLoanProduct
        >();
        const cfToLpMap = new Map<string, string[]>();
        const loanProductMap = new Map<string, string>();
        // if we are creating a new strategy, set all eligibility criteria to be excluded as a default
        const addDefaultEligibilityExclusions = strategyId === undefined;
        const eligibilityExclusionMap = new Map<string, Set<string>>();
        // @dmads98 consolidate with common-frontend util method to parse borrowing base mechanics
        data.creditFacilitiesForAssignmentGrouping.forEach((creditFacility) => {
          if (!creditFacilityIdForBorrower) {
            setCreditFacilityIdForBorrower(creditFacility.id);
          }
          creditFacility.borrowingBaseMechanicsForLoanProducts.forEach(
            (loanProduct) => {
              finleyAssert(
                loanProduct.implementationId === implementationId,
                new commonError.FinleyError(
                  'Loan product is not part of current mesh',
                  {
                    metadata: {
                      logMeta: {
                        loanProduct,
                        implementationId,
                      },
                    },
                  },
                ),
              );

              const baseFields = JSON.parse(loanProduct.baseFields);
              finleyAssert(
                typeguards.creditAgreement.isBaseFieldConfigArray(baseFields),
                new commonError.TypeError({
                  resourceType: 'BaseFieldConfigArray',
                  typeguardName: 'isBaseFieldConfigArray',
                  metadata: {
                    logMeta: {
                      loanProductId: loanProduct.loanProductId,
                      creditFacilityId: creditFacility.id,
                    },
                  },
                }),
              );
              loanProductMap.set(
                loanProduct.loanProductId,
                loanProduct.loanProductName,
              );
              // TODO: add support for intermediate fields also
              newBorrowingBaseMechanicsMap.set(
                `${creditFacility.id}+${loanProduct.loanProductId}`,
                {
                  ...loanProduct,
                  baseFields,
                },
              );
              const loanProducts = cfToLpMap.get(creditFacility.id) ?? [];
              loanProducts.push(loanProduct.loanProductId);
              cfToLpMap.set(creditFacility.id, loanProducts);
              if (addDefaultEligibilityExclusions) {
                const eligibilityCriteriaSet = new Set<string>([
                  ...loanProduct.eligibilityCriteria.map(
                    (criteria) => criteria.eligibilityCriteriaKey,
                  ),
                ]);
                eligibilityExclusionMap.set(
                  `${creditFacility.id}+${loanProduct.loanProductId}`,
                  eligibilityCriteriaSet,
                );
              }
            },
          );
        });
        setLoanProductNameMap(loanProductMap);
        setBorrowingBaseMechanicsMap(newBorrowingBaseMechanicsMap);
        setValidLoanProductsForCreditFacilitiesMap(cfToLpMap);
        if (addDefaultEligibilityExclusions) {
          setCurrentEligibilityExclusionsMap(eligibilityExclusionMap);
        }
      },
    },
  );

  useEffect(() => {
    if (validLoanProductsForCreditFacilitiesMap) {
      if (initialDecisionHeuristic) {
        switch (initialDecisionHeuristic.heuristicType) {
          case 'assignByTotalAssignableExposure':
            setHeuristicConfig(
              initTotalAssignableExposureDecisionHeuristic(
                validLoanProductsForCreditFacilitiesMap,
                initialDecisionHeuristic?.config,
              ),
            );
            break;
          case 'waterfallByExcessMargin':
            setHeuristicConfig(
              initWaterfallByExcessMargin(initialDecisionHeuristic),
            );
            break;
          default:
            finleyAssertUnreachable(initialDecisionHeuristic);
        }

        // default to TotalAssignableExposureDecisionHeuristic
      } else {
        setHeuristicConfig(
          initTotalAssignableExposureDecisionHeuristic(
            validLoanProductsForCreditFacilitiesMap,
          ),
        );
      }
    }
  }, [initialDecisionHeuristic, validLoanProductsForCreditFacilitiesMap]);

  const [
    getLatestInitialAssignmentSummary,
  ] = useGetLatestInitialAssignmentSummaryLazyQueryGQLType({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      // this isn't guaranteed to exist during initial setup
      // we probably don't need to be toasting here, and seems like it is okay
      // if undefined
      errorToastConfig.title = getUnexpectedErrorMessage(err);

      // this is not a good way to handle this error, we can do something better
      // in future
      if (
        errorToastConfig.title !== 'assignmentReceivableContainer must exist'
      ) {
        toast(errorToastConfig);
      }
    },
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data.latestAssignmentSummary) {
        const initialSummaryPreview = parseAssignmentSummaryPreview(
          data.latestAssignmentSummary,
          true,
        );
        setTotalAssignableExposureByLoanProductId(
          initialSummaryPreview.borrowingBaseReport
            .totalAssignableExposureByLoanProductId,
        );
      }
    },
  });

  const [
    createOrUpdateAssignmentStrategy,
    { loading: createOrUpdateAssignmentStrategyLoading },
  ] = useCreateOrUpdateAssignmentStrategyMutation({
    onCompleted: () => {
      successToastConfig.id = _.uniqueId(`assignmentStrategyForm-toast`);
      successToastConfig.title = `Successfully ${
        !strategyId ? 'created' : 'updated'
      } the ${strategyName} assignment strategy`;
      toast(successToastConfig);
    },
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  const [
    createReceivableResponseTapePreviewsFromAssignmentStrategy,
    { loading: createReceivableResponseTapePreviewsLoading },
  ] = useCreateReceivableResponseTapePreviewsFromAssignmentStrategyMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  useEffect(() => {
    if (creditFacilityIdForBorrower) {
      getLatestInitialAssignmentSummary({
        variables: {
          creditFacilityId: creditFacilityIdForBorrower,
          implementationId,
        },
      });
    }
  }, [
    creditFacilityIdForBorrower,
    getLatestInitialAssignmentSummary,
    implementationId,
  ]);

  const isFormDataValid = () => {
    let validationResult = true;
    if (strategyName.length === 0) {
      setStrategyNameError(true);
      validationResult = false;
    }
    const loanProductCriteriaKeySet = new Set<string>();
    currentLoanProductCriteria.forEach((loanProductCriteria, index) => {
      if (
        !loanProductCriteria.creditFacilityId ||
        !loanProductCriteria.loanProductId
      ) {
        validationResult = false;
        setShowIncompleteLoanProductCriteriaAlert(true);
      } else {
        const loanProductCriteriaKey = `${loanProductCriteria.creditFacilityId}+${loanProductCriteria.loanProductId}`;
        if (loanProductCriteriaKeySet.has(loanProductCriteriaKey)) {
          validationResult = false;
          setShowDuplicateLoanProductCriteriaAlert(true);
        }
        loanProductCriteriaKeySet.add(loanProductCriteriaKey);
        const exclusionSqlState = currentExclusionSqlClauseStates.current.get(
          index,
        );
        if (exclusionSqlState && !exclusionSqlState.isValid) {
          validationResult = false;
          setShowExclusionSqlClauseRuleErrors(true);
          setShowInvalidExclusionSqlClausesAlert(true);
          setTimeout(() => {
            setShowInvalidExclusionSqlClausesAlert(false);
          }, 10000);
        }
      }
    });

    if (heuristicConfig) {
      switch (heuristicConfig.heuristicType) {
        case 'assignByTotalAssignableExposure':
          heuristicConfig?.config.loanProductConfigs.forEach(
            (loanProductConfig) => {
              const totalFacilityRatios = _.sumBy(
                loanProductConfig.creditFacilityAllocations,
                ({ allocation: currAllocation }) => currAllocation,
              );
              if (totalFacilityRatios > 1) {
                validationResult = false;
                setShowInvalidHeuristicAllocationsAlert(true);
              }
            },
          );
          break;
        case 'waterfallByExcessMargin':
          heuristicConfig.config.waterfall.forEach((config) => {
            if (config.percentageRate && config.percentageRate < 0) {
              validationResult = false;
              setShowNegativeWaterfallValueAlert(true);
            }
            if (config.value && config.value < 0) {
              validationResult = false;
              setShowNegativeWaterfallValueAlert(true);
            }
          });
          break;
        default:
          finleyAssertUnreachable(heuristicConfig);
      }
    }

    return validationResult;
  };

  const getLoanProductCriteriaIdsToDelete = () => {
    const loanProductIdsToDelete = new Set<string>(
      initialLoanProductCriteria?.map((item) => item.id),
    );
    currentLoanProductCriteria.forEach((loanProductCriteria, index) => {
      if (loanProductCriteria.id) {
        const origLoanProductCriteria = initialLoanProductCriteriaMap.get(
          loanProductCriteria.id,
        );
        const newExclusionSqlClause =
          currentExclusionSqlClauseStates.current.get(index)
            ?.exclusionSqlClause ?? loanProductCriteria.exclusionSqlClause;
        if (
          origLoanProductCriteria &&
          loanProductCriteria.creditFacilityId ===
            origLoanProductCriteria.creditFacilityId &&
          loanProductCriteria.loanProductId ===
            origLoanProductCriteria.loanProductId &&
          origLoanProductCriteria.exclusionSqlClause === newExclusionSqlClause
        ) {
          loanProductIdsToDelete.delete(loanProductCriteria.id);
        }
      }
    });
    return loanProductIdsToDelete;
  };

  const getLoanProductCriteriaObjectsToCreate = (
    loanProductCriteriaIdsToDelete: Set<string>,
  ) => {
    const loanProductCriteriaObjs: {
      creditFacilityId: string;
      loanProductId: string;
      exclusionSqlClause: string;
    }[] = [];
    currentLoanProductCriteria.forEach((loanProductCriteria, index) => {
      const newExclusionSqlClause = currentExclusionSqlClauseStates.current.get(
        index,
      )?.exclusionSqlClause;
      if (
        loanProductCriteria.creditFacilityId &&
        loanProductCriteria.loanProductId &&
        newExclusionSqlClause
      ) {
        if (
          !loanProductCriteria.id ||
          loanProductCriteriaIdsToDelete.has(loanProductCriteria.id)
        ) {
          loanProductCriteriaObjs.push({
            creditFacilityId: loanProductCriteria.creditFacilityId,
            loanProductId: loanProductCriteria.loanProductId,
            exclusionSqlClause: newExclusionSqlClause,
          });
        }
      }
    });
    return loanProductCriteriaObjs;
  };

  const getEligibilityExclusionIdsToDelete = () => {
    const eligibilityExclusionIdsToDelete = new Set<string>([
      ...initialEligibilityExclusionMap.values(),
    ]);
    currentEligibilityExclusionsMap.forEach(
      (eligibilityCriteriaKeySet, creditFacilityLoanProductKey) => {
        eligibilityCriteriaKeySet.forEach((criteriaKey) => {
          const possibleId = initialEligibilityExclusionMap.get(
            `${creditFacilityLoanProductKey}+${criteriaKey}`,
          );
          if (possibleId) {
            eligibilityExclusionIdsToDelete.delete(possibleId);
          }
        });
      },
    );
    return eligibilityExclusionIdsToDelete;
  };

  const getEligibilityExclusionObjectsToCreate = (
    eligibilityExclusionIdsToDelete: Set<string>,
  ) => {
    const eligibilityExclusionObjs: {
      creditFacilityId: string;
      loanProductId: string;
      eligibilityCriteriaKey: string;
    }[] = [];
    currentEligibilityExclusionsMap.forEach(
      (eligibilityCriteriaKeySet, creditFacilityLoanProductKey) => {
        eligibilityCriteriaKeySet.forEach((criteriaKey) => {
          const possibleId = initialEligibilityExclusionMap.get(
            `${creditFacilityLoanProductKey}+${criteriaKey}`,
          );
          if (!possibleId || eligibilityExclusionIdsToDelete.has(possibleId)) {
            const keyInfo = creditFacilityLoanProductKey.split('+');
            eligibilityExclusionObjs.push({
              creditFacilityId: keyInfo[0],
              loanProductId: keyInfo[1],
              eligibilityCriteriaKey: criteriaKey,
            });
          }
        });
      },
    );
    return eligibilityExclusionObjs;
  };

  const getFormText = (isHeader: boolean) => {
    if (!strategyId) {
      return 'Create Strategy';
    }
    if (readOnly) {
      return 'Apply Strategy';
    }
    return `${isHeader ? 'Edit' : 'Save'} Strategy`;
  };

  const formSubmit = async () => {
    if (readOnly && strategyId) {
      const response = await createReceivableResponseTapePreviewsFromAssignmentStrategy(
        {
          variables: {
            implementationId,
            strategyId,
          },
        },
      );
      if (response.data) {
        const previewJobId =
          response.data
            .createReceivableResponseTapePreviewsFromAssignmentStrategy;
        navigate(
          `/assignments/${implementationId}/summary?previewJobId=${previewJobId}`,
        );
      }
    } else if (isFormDataValid()) {
      setShowExclusionSqlClauseRuleErrors(false);
      setShowInvalidExclusionSqlClausesAlert(false);
      setShowInvalidHeuristicAllocationsAlert(false);
      setShowNegativeWaterfallValueAlert(false);
      let loanProductCriteriaIdsToDelete = new Set<string>();
      let eligibilityExclusionIdsToDelete = new Set<string>();
      if (strategyId) {
        loanProductCriteriaIdsToDelete = getLoanProductCriteriaIdsToDelete();
        eligibilityExclusionIdsToDelete = getEligibilityExclusionIdsToDelete();
      }
      const loanProductCriteriaObjectsToCreate = getLoanProductCriteriaObjectsToCreate(
        loanProductCriteriaIdsToDelete,
      );
      const eligibilityExclusionObjectsToCreate = getEligibilityExclusionObjectsToCreate(
        eligibilityExclusionIdsToDelete,
      );

      const newAssignmentStrategy = await createOrUpdateAssignmentStrategy({
        variables: {
          strategyName,
          description,
          implementationId,
          strategyId,
          loanProductCriteria: loanProductCriteriaObjectsToCreate,
          loanProductCriteriaIdsToDelete: [...loanProductCriteriaIdsToDelete],
          eligibilityExclusions: eligibilityExclusionObjectsToCreate,
          eligibilityExclusionIdsToDelete: [...eligibilityExclusionIdsToDelete],
          decisionHeuristicConfig: JSON.stringify(heuristicConfig),
        },
      });
      if (newAssignmentStrategy.data) {
        const newStrategyId =
          newAssignmentStrategy.data.createOrUpdateAssignmentStrategy.id;
        if (!strategyId) {
          navigate(`/settings/assignment-strategy/${newStrategyId}/edit`);
        } else if (refetchAssignmentStrategy) {
          refetchAssignmentStrategy();
        }
      }
    }
  };

  return (
    <Flex direction="column" overflowX="auto">
      <Flex justify="space-between" mb="24px">
        <Heading as="h2">{getFormText(true)}</Heading>
        <Spacer />
      </Flex>
      {readOnly && strategyId ? (
        <Text fontSize="md" mb="48px" color="gray">
          Review your assignment strategy. You can edit your strategy on the{' '}
          <Link
            style={{ color: colors.blue[400], textDecoration: 'underline' }}
            to={`/settings/assignment-strategy/${strategyId}/edit`}
          >
            Settings
          </Link>{' '}
          page.
        </Text>
      ) : (
        <Text fontSize="md" mb="48px" color="gray">
          {!strategyId ? 'Create a new' : 'Edit your'} receivable assignment
          strategy.
        </Text>
      )}
      {creditFacilitiesForAssignmentGroupingDataLoading && (
        <Skeleton height="80vh" />
      )}
      {creditFacilitiesForAssignmentGroupingData &&
        validLoanProductsForCreditFacilitiesMap &&
        loanProductNameMap &&
        heuristicConfig && (
          <>
            <Flex mb="8px">
              <Flex
                direction="column"
                mr="24px"
                minWidth="300px"
                maxWidth="300px"
              >
                <Text fontSize="md" mb="16px" fontWeight="600">
                  Strategy Name
                </Text>
                <Text fontSize="md" mb="16px" color="gray">
                  Enter a unique strategy name.
                </Text>
              </Flex>
              <Flex direction="column" width="100%">
                <Input
                  placeholder={readOnly ? '' : 'Enter name'}
                  size="lg"
                  value={strategyName}
                  onChange={(event) => {
                    if (event.target.value.length <= 255) {
                      setStrategyName(event.target.value);
                      setStrategyNameError(false);
                    }
                  }}
                  isInvalid={strategyNameError}
                  errorBorderColor="red.400"
                  disabled={readOnly}
                />
                {strategyNameError && (
                  <Text fontSize="sm" mt="4px" color="red">
                    Strategy name is a required field.
                  </Text>
                )}
              </Flex>
            </Flex>
            <Divider />
            <Flex mt="24px" mb="8px">
              <Flex
                direction="column"
                mr="24px"
                minWidth="300px"
                maxWidth="300px"
              >
                <Text fontSize="md" mb="16px" fontWeight="600">
                  Description
                </Text>
                <Text fontSize="md" mb="16px" color="gray">
                  Add a description.
                </Text>
              </Flex>
              <Input
                placeholder={readOnly ? '' : 'Enter description'}
                size="lg"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                disabled={readOnly}
              />
            </Flex>
            <Divider />

            <EligibilityExclusions
              currentEligibilityExclusionsMap={currentEligibilityExclusionsMap}
              setCurrentEligibilityExclusionsMap={
                setCurrentEligibilityExclusionsMap
              }
              creditFacilitiesForAssignmentGrouping={creditFacilitiesForAssignmentGroupingData.creditFacilitiesForAssignmentGrouping.filter(
                (creditFacility) =>
                  creditFacility.implementationId === implementationId,
              )}
              validLoanProductsForCreditFacilitiesMap={
                validLoanProductsForCreditFacilitiesMap
              }
              borrowingBaseMechanicsMap={borrowingBaseMechanicsMap}
              readOnly={readOnly}
            />
            <Divider />

            <ExclusionCriteriaSection
              currentLoanProductCriteria={currentLoanProductCriteria}
              setCurrentLoanProductCriteria={setCurrentLoanProductCriteria}
              creditFacilitiesForAssignmentGrouping={creditFacilitiesForAssignmentGroupingData.creditFacilitiesForAssignmentGrouping.filter(
                (creditFacility) =>
                  creditFacility.implementationId === implementationId,
              )}
              currentExclusionSqlClauseStates={currentExclusionSqlClauseStates}
              showExclusionSqlClauseRuleErrors={
                showExclusionSqlClauseRuleErrors
              }
              validLoanProductsForCreditFacilitiesMap={
                validLoanProductsForCreditFacilitiesMap
              }
              borrowingBaseMechanicsMap={borrowingBaseMechanicsMap}
              readOnly={readOnly}
            />

            {!readOnly && (
              <Select
                buttonProps={{ mt: '20px', width: '400px' }}
                value={heuristicConfig.heuristicType}
                options={[
                  {
                    label: 'Assign By Total Assignable Exposure',
                    value: 'assignByTotalAssignableExposure' as const,
                  },
                  {
                    label: 'Assign By Waterfall',
                    value: 'waterfallByExcessMargin' as const,
                  },
                ]}
                onChange={(val) => {
                  if (val) {
                    switch (val) {
                      case 'assignByTotalAssignableExposure':
                        setHeuristicConfig(
                          initTotalAssignableExposureDecisionHeuristic(
                            validLoanProductsForCreditFacilitiesMap,
                          ),
                        );
                        break;
                      case 'waterfallByExcessMargin':
                        setHeuristicConfig(initWaterfallByExcessMargin());
                        break;
                      default:
                        finleyAssertUnreachable(val);
                    }
                  }
                }}
              />
            )}

            {heuristicConfig.heuristicType ===
              'assignByTotalAssignableExposure' && (
              <DecisionHeuristicByTotalAssignableExposure
                heuristicConfig={heuristicConfig.config}
                setHeuristicConfig={setHeuristicConfig}
                totalAssignableExposureByLoanProductId={
                  totalAssignableExposureByLoanProductId
                }
                creditFacilitiesForAssignmentGroupingData={
                  creditFacilitiesForAssignmentGroupingData
                }
                loanProductNameMap={loanProductNameMap}
                readOnly={readOnly}
              />
            )}

            {heuristicConfig.heuristicType === 'waterfallByExcessMargin' && (
              <WaterfallByExcessMargin
                heuristicConfig={heuristicConfig}
                setHeuristicConfig={setHeuristicConfig}
                creditFacilitiesForAssignmentGroupingData={
                  creditFacilitiesForAssignmentGroupingData
                }
                readOnly={readOnly}
              />
            )}

            {showDuplicateLoanProductCriteriaAlert && (
              <Alert status="error" mb="10px" mt="10px">
                <AlertIcon />
                <AlertDescription>
                  There are multiple loan product criteria for the same credit
                  facility and loan product.
                </AlertDescription>
              </Alert>
            )}
            {showIncompleteLoanProductCriteriaAlert && (
              <Alert status="error" mb="10px" mt="10px">
                <AlertIcon />
                <AlertDescription>
                  Some loan product criteria are missing a credit facility
                  and/or loan product.
                </AlertDescription>
              </Alert>
            )}
            {showInvalidExclusionSqlClausesAlert && (
              <Alert status="error" mb="10px" mt="10px">
                <AlertIcon />
                <AlertDescription>
                  Some loan product criteria have invalid exclusion criteria.
                  Correct any errors to save strategy.
                </AlertDescription>
              </Alert>
            )}
            {showInvalidHeuristicAllocationsAlert && (
              <Alert status="error" mb="10px" mt="10px">
                <AlertIcon />
                <AlertDescription>
                  Some loan products have decision heuristics whose allocations
                  exceed 100%. Correct any errors to save strategy.
                </AlertDescription>
              </Alert>
            )}
            {showNegativeWaterfallValueAlert && (
              <Alert status="error" mb="10px" mt="10px">
                <AlertIcon />
                <AlertDescription>
                  Negative percentages / values cannot be specified in waterfall
                  heuristic. Correct any errors to save strategy.
                </AlertDescription>
              </Alert>
            )}
            <Flex justifyContent="space-between" mt="24px">
              <Button
                leftIcon={<Icon icon="chevron-left" />}
                variant="ghost"
                onClick={() => {
                  navigate(backUrl);
                }}
              >
                Back
              </Button>
              <Button
                colorScheme="aurora"
                onClick={formSubmit}
                isLoading={
                  createOrUpdateAssignmentStrategyLoading ||
                  createReceivableResponseTapePreviewsLoading
                }
              >
                {getFormText(false)}
              </Button>
            </Flex>
          </>
        )}
    </Flex>
  );
};
